import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

// Import Socket Provider
import { SocketProvider } from './context/SocketContext';

// Import components
import Login from './components/Login';
import HomePage from './pages/HomePage';
import SignupPage from './pages/SignupPage';
import Student from './pages/Student';
import Teacher from './pages/Teacher';
import Parent from './pages/Parent';
import AdminDashboard from './pages/AdminDashboard';
import ProtectedRoute from './components/ProtectedRoute';
import LandingPage from './pages/LandingPage';
import ContentPageLayout from './components/ContentPageLayout';
import TeacherProfile from './components/TeacherProfile';

// Import pages that exist or will exist
// import About from './pages/About';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';

function App() {
  return (
    <Router>
      <SocketProvider> {/* Wrap everything with SocketProvider */}
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/get-started" element={<SignupPage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/teacher-profile/:teacherId" element={<TeacherProfile />} />
          
          {/* About Section */}
          <Route path="/about" element={
            <ContentPageLayout title="About Us" subtitle="Learn about our mission">
              {/* Add About component when ready */}
            </ContentPageLayout>
          } />
          <Route path="/impact" element={
            <ContentPageLayout title="Our Impact" subtitle="See how we're making a difference">
            </ContentPageLayout>
          } />
          <Route path="/partners" element={
            <ContentPageLayout title="Partners" subtitle="Our valued partners">
            </ContentPageLayout>
          } />
          
          {/* Contact Section */}
          <Route path="/help" element={
            <ContentPageLayout title="Help Center" subtitle="Get help and support">
            </ContentPageLayout>
          } />
          <Route path="/support" element={
            <ContentPageLayout title="Support" subtitle="Community support">
            </ContentPageLayout>
          } />
          <Route path="/contact" element={
            <ContentPageLayout title="Contact Us" subtitle="Get in touch with our team">
              <Contact />
            </ContentPageLayout>
          } />
          <Route path="/feedback" element={
            <ContentPageLayout title="Feedback" subtitle="Share your feedback with us">
            </ContentPageLayout>
          } />
          
          {/* Subject Routes */}
          <Route path="/math" element={
            <ContentPageLayout title="Mathematics" subtitle="Comprehensive math resources">
            </ContentPageLayout>
          } />
          <Route path="/biology" element={
            <ContentPageLayout title="Biology" subtitle="Life sciences and biology resources">
            </ContentPageLayout>
          } />
          <Route path="/english" element={
            <ContentPageLayout title="English Language" subtitle="Language and literature resources">
            </ContentPageLayout>
          } />
          <Route path="/chemistry" element={
            <ContentPageLayout title="Chemistry" subtitle="Chemical sciences resources">
            </ContentPageLayout>
          } />
          <Route path="/economics" element={
            <ContentPageLayout title="Economics" subtitle="Economic principles and resources">
            </ContentPageLayout>
          } />
          <Route path="/physics" element={
            <ContentPageLayout title="Physics" subtitle="Physical sciences resources">
            </ContentPageLayout>
          } />
          
          {/* Additional Subjects */}
          <Route path="/government" element={
            <ContentPageLayout title="Government" subtitle="Civic education and governance">
            </ContentPageLayout>
          } />
          <Route path="/accounting" element={
            <ContentPageLayout title="Accounting" subtitle="Financial accounting resources">
            </ContentPageLayout>
          } />
          <Route path="/literature" element={
            <ContentPageLayout title="Literature" subtitle="Literary arts and analysis">
            </ContentPageLayout>
          } />
          <Route path="/further-maths" element={
            <ContentPageLayout title="Further Mathematics" subtitle="Advanced math concepts">
            </ContentPageLayout>
          } />
          <Route path="/history" element={
            <ContentPageLayout title="History" subtitle="Historical studies and resources">
            </ContentPageLayout>
          } />
          <Route path="/geography" element={
            <ContentPageLayout title="Geography" subtitle="Earth and environmental studies">
            </ContentPageLayout>
          } />
          <Route path="/commerce" element={
            <ContentPageLayout title="Commerce" subtitle="Business and trade resources">
            </ContentPageLayout>
          } />
          <Route path="/computer-science" element={
            <ContentPageLayout title="Computer Science" subtitle="Computing and technology">
            </ContentPageLayout>
          } />
          <Route path="/agricultural-science" element={
            <ContentPageLayout title="Agricultural Science" subtitle="Farming and agriculture">
            </ContentPageLayout>
          } />
          <Route path="/technical-drawing" element={
            <ContentPageLayout title="Technical Drawing" subtitle="Engineering and technical drawing">
            </ContentPageLayout>
          } />
          
          {/* Resources */}
          <Route path="/downloads" element={
            <ContentPageLayout title="Downloads" subtitle="Educational resources and materials">
            </ContentPageLayout>
          } />
          <Route path="/blog" element={
            <ContentPageLayout title="Blog" subtitle="Educational insights and updates">
            </ContentPageLayout>
          } />
          <Route path="/teachers" element={
            <ContentPageLayout title="For Teachers" subtitle="Resources for educators">
            </ContentPageLayout>
          } />
          <Route path="/parents" element={
            <ContentPageLayout title="For Parents" subtitle="Resources for parents">
            </ContentPageLayout>
          } />
          
          {/* Call to Action */}
          <Route path="/donate" element={
            <ContentPageLayout title="Donate" subtitle="Support our mission">
            </ContentPageLayout>
          } />
          <Route path="/volunteer" element={
            <ContentPageLayout title="Volunteer" subtitle="Join our volunteer community">
            </ContentPageLayout>
          } />
          
          {/* Policy */}
          <Route path="/terms" element={
            <ContentPageLayout title="Terms of Service" subtitle="Our terms and conditions">
              <Terms />
            </ContentPageLayout>
          } />
          <Route path="/privacy" element={
            <ContentPageLayout title="Privacy Policy" subtitle="Your privacy matters">
              <Privacy />
            </ContentPageLayout>
          } />
          <Route path="/cookies" element={
            <ContentPageLayout title="Cookie Policy" subtitle="Our cookie usage policy">
            </ContentPageLayout>
          } />
          
          {/* Girls for Tech */}
          <Route path="/girls-for-tech" element={
            <ContentPageLayout title="Girls for Tech" subtitle="Empowering girls in technology">
            </ContentPageLayout>
          } />
          <Route path="/training" element={
            <ContentPageLayout title="Teacher Training" subtitle="Professional development">
            </ContentPageLayout>
          } />
          <Route path="/demo" element={
            <ContentPageLayout title="Book a Demo" subtitle="See our platform in action">
            </ContentPageLayout>
          } />
          
          {/* Protected Dashboard Routes */}
          <Route path="/student" element={
            <ProtectedRoute requiredUserType="student">
              <Student />
            </ProtectedRoute>
          } />
          <Route path="/teacher" element={
            <ProtectedRoute requiredUserType="teacher">
              <Teacher />
            </ProtectedRoute>
          } />
          <Route path="/parent" element={
            <ProtectedRoute requiredUserType="parent">
              <Parent />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute requiredUserType="admin">
              <AdminDashboard />
            </ProtectedRoute>
          } />
          
          {/* Fallback Route */}
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </SocketProvider>
    </Router>
  );
}

export default App;